import { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Mail, ArrowRight, Star, Check, Flame, Sparkles, Utensils, Calendar, ChevronLeft, ChevronRight, Camera, Truck } from 'lucide-react';
import SEO from '../components/SEO';

export default function Home() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  const reviewsRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url // Mandatory honeypot field
        })
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }

      const result = await response.json();
      if (result.status === 'ok') {
        navigate('/tak-for-din-henvendelse');
      } else {
        throw new Error('Der opstod en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const popularDishes = [
    {
      title: "16. Thai Forårsruller",
      description: "5 stk. hjemmelavede forårsruller med glasnudler, gulerødder, forårsløg, bambus, spidskål og majs.",
      price: 70,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/sproede-foraarsruller.jpg",
      tag: "Hjemmelavet"
    },
    {
      title: "20. Kung Tod",
      description: "4 stk. indbagte tempurarejer med sød chilisauce.",
      price: 70,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/indbagte-rejer.jpg",
      tag: "Populær"
    },
    {
      title: "1. Red Curry 🌶",
      description: "Kylling i rød karry, kokosmælk, peberfrugt, bambusskud, basilikum, løg, gulerødder og chili.",
      price: 125,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-red-curry-premium.jpg",
      tag: "Klassiker"
    },
    {
      title: "11. Phat Thai",
      description: "Kylling eller rejer, risnudler, æg, blomkål, forårsløg, bønnespirer, gulerødder, hakket peanuts, spidskål og broccoli.",
      price: 125,
      image: "https://assets.auxodev.dk/cdn-cgi/image/width=400,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-pad-thai-premium.jpg",
      tag: "Favorit"
    }
  ];

  const carouselImages = [
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-red-curry-premium.jpg",
      title: "Rød Karry",
      category: "Hovedretter"
    },
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-pad-thai-premium.jpg",
      title: "Pad Thai",
      category: "Hovedretter"
    },
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/indbagte-rejer.jpg",
      title: "Indbagte Rejer",
      category: "Forretter"
    },
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/sproede-foraarsruller.jpg",
      title: "Sprøde Forårsruller",
      category: "Forretter"
    },
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/kylling-cashewnoedder.jpg",
      title: "Kylling med Cashewnødder",
      category: "Hovedretter"
    },
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/stegte-nudler-bami-goreng.jpg",
      title: "Stegte Nudler",
      category: "Hovedretter"
    },
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/groen-karry.jpg",
      title: "Kylling i Karry",
      category: "Hovedretter"
    },
    {
      url: "https://assets.auxodev.dk/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/phat-phak.jpg",
      title: "Klassisk Wok",
      category: "Hovedretter"
    }
  ];

  const reviews = [
    {
      name: "Simon Stitz Marquardsen",
      role: "Direktør, Stevnsgade Copenhagen Basketball",
      title: "Lækker mad til en fornuftig pris",
      text: "Thai Hot Chili leverer altid lækker mad til en fornuftig pris. Vi har med stor glæde brugt dem til flere af vores sponsor arrangementer. Mvh. Simon Marquardsen. Direktør. Stevnsgade Copenhagen Basketball"
    },
    {
      name: "Kurt Anker Valdemar Schou",
      role: "Lokal gæst",
      title: "Det bedste der er sket i området i mange år",
      text: "Det bedste der er sket i området i mange år. Vi bare Elsker deres mad..... Sidder i Grækenland og glæder mig til vi skal have deres mad igen..... Super søde er de jo også....det eneste som er ærgerligt er at de holder lukket onsdage og torsdage..."
    },
    {
      name: "Mette Frederiksen",
      role: "Privat fest, Catering",
      title: "Fantastisk service og mad!",
      text: "Vi bookede foodtrucken til vores sommerfest, og det var en kæmpe succes. Maden var utrolig lækker, frisk og autentisk, og betjeningen var i top. Alle gæster var begejstrede!"
    },
    {
      name: "Thomas Poulsen",
      role: "Firmafest, Catering",
      title: "Super professionelt og velsmagende",
      text: "Vi havde Thai Hot Chili til vores årlige firmafest med 80 personer. Maden blev serveret rygende varm direkte fra deres foodtruck. Deres Pad Thai og røde karry var en kæmpe favorit hos alle!"
    },
    {
      name: "Line Jensen",
      role: "Take-away stamkunde",
      title: "Bedste thaimad i Nordsjælland",
      text: "Jeg henter mad her mindst en gang om ugen. Råvarerne er altid friske, grøntsagerne er sprøde, og smagen er helt som i Thailand. Særligt deres indbagte rejer og grønne karry kan varmt anbefales!"
    },
    {
      name: "Henrik Nielsen",
      role: "Fødselsdagsfest",
      title: "Gennemført oplevelse",
      text: "Fantastisk mad og utrolig venligt personale. De var super fleksible med menuen, så der også var gode vegetariske muligheder til vores gæster. Foodtrucken skabte en helt særlig stemning i haven."
    }
  ];

  return (
    <>
      <SEO
        title="Autentisk thai mad i Skibby | Bestil take-away eller en foodtruck"
        description="Autentisk thai mad i Skibby | Bestil take-away eller en foodtruck. Vi tilbyder lækker thailandsk streetfood med god smag til hverdag og fest."
      />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] lg:min-h-[80vh] flex flex-col lg:block overflow-hidden bg-white text-brand-dark">
        {/* Background Watermark */}
        <div className="absolute left-[-5%] bottom-[-5%] text-gray-100/50 pointer-events-none select-none z-0 hidden md:block">
          <Truck className="w-[350px] h-[350px] opacity-[0.06]" strokeWidth={0.75} />
        </div>

        {/* Left Side: Content aligned to container */}
        <div className="w-full max-w-7xl mx-auto px-6 pt-36 pb-24 md:pt-48 md:pb-32 lg:pt-56 lg:pb-64 flex items-center min-h-[55vh] lg:min-h-[80vh] relative z-10">
          <div className="w-full lg:w-1/2 lg:pr-12 flex flex-col justify-center">
            <div className="max-w-xl py-4 my-auto flex flex-col justify-center">
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-brand-dark leading-tight">
                Autentisk thai mad i Skibby
              </h1>
              <p className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed">
                Frisk, smagfuld og autentisk thailandsk madlavning lavet med kærlighed og friske råvarer.
              </p>
              <div className="mt-8">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-5 py-3 md:px-8 md:py-4 rounded-full transition-all shadow-md text-xs md:text-sm uppercase tracking-wider"
                >
                  Kontakt os
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side: Full-bleed Image */}
        <div className="w-full lg:w-1/2 h-[350px] sm:h-[450px] lg:h-full lg:absolute lg:right-0 lg:top-0 lg:bottom-0 relative shrink-0 sm:border-t-8 sm:border-brand-green lg:border-t-0 lg:border-l-8 lg:border-brand-green xl:border-l-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/imgi-29-rejer2.webp"
            alt="Autentisk Thai Rejer"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Foodtruck Showcase Section */}
      <section className="relative py-16 md:py-20 bg-gray-50 text-brand-dark overflow-hidden">
        {/* Background Watermark */}
        <div className="absolute right-[-8%] top-[-8%] text-gray-200/40 pointer-events-none select-none z-0 hidden lg:block">
          <Truck className="w-[450px] h-[450px] opacity-[0.05]" strokeWidth={0.5} />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Column: Foodtruck Image */}
            <div className="lg:col-span-6 flex justify-center lg:justify-start order-2 lg:order-1">
              <div className="relative group w-full overflow-hidden rounded-3xl shadow-md">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/imgi-17-foodtruck1.jpg"
                  alt="Thai Hot Chili Foodtruck"
                  className="w-full h-auto object-cover aspect-[4/3] transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            </div>

            {/* Right Column: Text Content */}
            <div className="lg:col-span-6 flex flex-col gap-6 order-1 lg:order-2">
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold leading-tight text-brand-dark">
                Gør din næste fest uforglemmelig med vores foodtruck
              </h2>
              <p className="text-gray-600 leading-relaxed text-base sm:text-lg">
                Booking af foodtrucken til f.eks. bryllup, konfirmationer, sportsarrangementer, events, after party, fødselsdage mm.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2">
                <div className="flex items-center gap-3 py-1">
                  <Check className="w-5 h-5 text-black shrink-0 stroke-[3]" />
                  <span className="font-medium text-sm text-gray-800">Catering til firmafester</span>
                </div>
                <div className="flex items-center gap-3 py-1">
                  <Check className="w-5 h-5 text-black shrink-0 stroke-[3]" />
                  <span className="font-medium text-sm text-gray-800">Bryllupper & Konfirmationer</span>
                </div>
                <div className="flex items-center gap-3 py-1">
                  <Check className="w-5 h-5 text-black shrink-0 stroke-[3]" />
                  <span className="font-medium text-sm text-gray-800">Fødselsdage & Jubilæer</span>
                </div>
                <div className="flex items-center gap-3 py-1">
                  <Check className="w-5 h-5 text-black shrink-0 stroke-[3]" />
                  <span className="font-medium text-sm text-gray-800">Markeder & Events</span>
                </div>
              </div>

              <div className="mt-6">
                <Link
                  to="/foodtruck-catering"
                  className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-6 py-3.5 rounded-full transition-all shadow-md text-sm uppercase tracking-wider"
                >
                  Læs mere om foodtrucken
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Dishes Section */}
      <section className="py-24 bg-white text-brand-dark">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 flex flex-col gap-4">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold">
              Populære retter fra menukortet
            </h2>
            <p className="text-gray-600">
              Smag på det ægte Thailand. Vi tilbereder alle retter med friske råvarer og masser af kærlighed.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {popularDishes.slice(0, 3).map((dish, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm flex flex-col h-full"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={dish.image}
                    alt={dish.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6 flex flex-col flex-grow gap-3">
                  <h3 className="font-display text-lg font-bold text-brand-dark">
                    {dish.title}
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed flex-grow">
                    {dish.description}
                  </p>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                    <span className="text-xs font-semibold text-gray-500 uppercase">Pris</span>
                    <span className="font-display text-lg font-bold text-brand-primary">
                      kr. {dish.price},-
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/menukort"
              className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-8 py-4 rounded-full transition-all shadow-md"
            >
              Se hele det digitale menukort
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Full-width Food Gallery Carousel */}
      <section className="py-16 bg-white text-brand-dark overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-brand-dark">
              Vores appetitvækkende madgalleri
            </h2>
            <p className="text-gray-600 mt-3">
              Gå på opdagelse i vores farverige univers af frisklavede, autentiske thailandske retter.
            </p>
          </div>
          
          {/* Navigation Arrows */}
          <div className="flex items-center gap-3 self-start md:self-auto">
            <button
              onClick={() => {
                if (carouselRef.current) {
                  carouselRef.current.scrollBy({ left: -400, behavior: 'smooth' });
                }
              }}
              className="w-12 h-12 rounded-full border border-gray-200 bg-white flex items-center justify-center text-brand-dark hover:bg-brand-dark hover:text-white hover:border-brand-dark transition-all duration-200 shadow-sm cursor-pointer"
              aria-label="Forrige billede"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => {
                if (carouselRef.current) {
                  carouselRef.current.scrollBy({ left: 400, behavior: 'smooth' });
                }
              }}
              className="w-12 h-12 rounded-full border border-gray-200 bg-white flex items-center justify-center text-brand-dark hover:bg-brand-dark hover:text-white hover:border-brand-dark transition-all duration-200 shadow-sm cursor-pointer"
              aria-label="Næste billede"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Full-width Carousel Container */}
        <div className="w-full relative px-4 md:px-0">
          <div
            ref={carouselRef}
            className="flex gap-3 overflow-x-auto scrollbar-none py-4 px-4 md:px-12 scroll-smooth snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {carouselImages.map((image, index) => (
              <div
                key={index}
                className="snap-start shrink-0 w-[280px] sm:w-[380px] md:w-[calc((100%-24px)/3)] aspect-[4/3] overflow-hidden relative group transition-all duration-300"
              >
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/${image.url.split('/').pop()}`}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="text-center mt-10">
          <Link
            to="/galleri"
            className="inline-flex items-center gap-2 border border-brand-dark/20 hover:border-brand-primary text-brand-dark hover:text-brand-primary font-semibold px-6 py-3 rounded-full transition-all text-sm uppercase tracking-wider"
          >
            <Camera className="w-4 h-4" />
            Se hele galleriet
          </Link>
        </div>
      </section>



      {/* Reviews Section */}
      <section className="py-12 md:py-16 bg-gray-50 text-brand-dark overflow-hidden">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-brand-dark">
                Hvad vores gæster siger
              </h2>
              <p className="text-gray-600 mt-3">
                Læs anmeldelser fra vores glade kunder og samarbejdspartnere.
              </p>
            </div>

            {/* Navigation Arrows */}
            <div className="flex items-center gap-3 self-start md:self-auto">
              <button
                onClick={() => {
                  if (reviewsRef.current) {
                    reviewsRef.current.scrollBy({ left: -400, behavior: 'smooth' });
                  }
                }}
                className="w-12 h-12 rounded-full border border-gray-200 bg-white flex items-center justify-center text-brand-dark hover:bg-brand-dark hover:text-white hover:border-brand-dark transition-all duration-200 shadow-sm cursor-pointer"
                aria-label="Forrige anmeldelse"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  if (reviewsRef.current) {
                    reviewsRef.current.scrollBy({ left: 400, behavior: 'smooth' });
                  }
                }}
                className="w-12 h-12 rounded-full border border-gray-200 bg-white flex items-center justify-center text-brand-dark hover:bg-brand-dark hover:text-white hover:border-brand-dark transition-all duration-200 shadow-sm cursor-pointer"
                aria-label="Næste anmeldelse"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Scrollable Carousel Container */}
          <div
            ref={reviewsRef}
            className="flex gap-8 overflow-x-auto scrollbar-none py-4 px-1 md:px-0 scroll-smooth snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {reviews.map((review, index) => (
              <div
                key={index}
                className="snap-start shrink-0 w-[290px] sm:w-[380px] md:w-[calc((100%-32px)/2)] lg:w-[calc((100%-64px)/3)] bg-white border border-gray-100 p-8 rounded-2xl flex flex-col justify-between shadow-sm transition-all duration-300 hover:shadow-md"
              >
                <div>
                  <div className="flex items-center gap-1 mb-4 text-brand-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-current" />
                    ))}
                  </div>
                  <h3 className="font-display text-lg font-bold text-brand-dark mb-2">
                    &ldquo;{review.title}&rdquo;
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">
                    &ldquo;{review.text}&rdquo;
                  </p>
                </div>
                <div className="border-t border-gray-100 pt-4 mt-auto">
                  <p className="font-semibold text-brand-dark text-sm">{review.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{review.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-16 bg-white text-brand-dark">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-8">
            Vores Samarbejdspartnere
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16 lg:gap-20">
            <a
              href="https://www.tastytown.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100 flex items-center justify-center"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/street-food-0.png"
                alt="Tasty Town Logo"
                className="h-14 w-auto object-contain"
              />
            </a>
            <a
              href="https://www.ringstedfestival.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100 flex items-center justify-center"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/ringsted-festival-0.png"
                alt="Ringsted Festival Logo"
                className="h-14 w-auto object-contain"
              />
            </a>
            <a
              href="https://www.thornico.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100 flex items-center justify-center"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thornico.png"
                alt="Thornico Logo"
                className="h-14 w-auto object-contain"
              />
            </a>
            <a
              href="https://nuento.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100 flex items-center justify-center"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/nuento-logo-primary-sukznzifus.jpg"
                alt="Nuento Logo"
                className="h-14 w-auto object-contain"
              />
            </a>
            <a
              href="https://smukevent.dk/"
              target="_blank"
              rel="noopener noreferrer"
              className="grayscale hover:grayscale-0 transition-all opacity-60 hover:opacity-100 flex items-center justify-center bg-brand-dark px-4 py-2.5 rounded-lg"
            >
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=200,quality=90,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/smukevent-logo-06-300x91.png"
                alt="Smukevent Logo"
                className="h-10 w-auto object-contain"
              />
            </a>
          </div>
        </div>
      </section>

      {/* Booking CTA Section */}
      <section className="relative py-24 text-white overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-pad-thai-premium.jpg"
            alt="Autentisk Pad Thai Close-up"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/60 to-black/90 md:from-black/40 md:via-black/70 md:to-black/95 backdrop-blur-[1px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center text-left">
          {/* Left Column: Text & CTA */}
          <div className="flex flex-col gap-6">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold leading-tight">
              Skal vi stå for maden til din næste fest?
            </h2>
            <p className="text-white/90 text-base sm:text-lg leading-relaxed">
              Kontakt os i dag for at høre mere om vores catering-menuer og book vores populære foodtruck. Vi glæder os til at høre fra dig!
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 mt-4">
              <a
                href="tel:+4552901306"
                className="w-full sm:w-auto flex items-center justify-center gap-3 bg-brand-primary hover:bg-brand-primary/90 text-white font-semibold px-8 py-4 rounded-full transition-all shadow-lg hover:shadow-xl"
              >
                <Phone className="w-5 h-5" />
                Ring til foodtruck (52 90 13 06)
              </a>
            </div>
          </div>

          {/* Right Column: Webform */}
          <div className="w-full">
            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
              {/* Name */}
              <div className="flex flex-col gap-1">
                <label htmlFor="home-name" className="text-xs font-bold uppercase tracking-wider text-white">
                  Navn
                </label>
                <input
                  type="text"
                  id="home-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Dit fulde navn"
                  className="w-full bg-transparent border-b-2 border-white/80 focus:border-white text-white placeholder-white/60 py-2 outline-none transition-colors font-medium"
                />
              </div>

              {/* Phone */}
              <div className="flex flex-col gap-1">
                <label htmlFor="home-phone" className="text-xs font-bold uppercase tracking-wider text-white">
                  Telefonnummer
                </label>
                <input
                  type="tel"
                  id="home-phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="Dit telefonnummer"
                  className="w-full bg-transparent border-b-2 border-white/80 focus:border-white text-white placeholder-white/60 py-2 outline-none transition-colors font-medium"
                />
              </div>

              {/* Email */}
              <div className="flex flex-col gap-1">
                <label htmlFor="home-email" className="text-xs font-bold uppercase tracking-wider text-white">
                  E-mail
                </label>
                <input
                  type="email"
                  id="home-email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="Din e-mailadresse"
                  className="w-full bg-transparent border-b-2 border-white/80 focus:border-white text-white placeholder-white/60 py-2 outline-none transition-colors font-medium"
                />
              </div>

              {/* Message */}
              <div className="flex flex-col gap-1">
                <label htmlFor="home-message" className="text-xs font-bold uppercase tracking-wider text-white">
                  Beskrivelse af dit arrangement
                </label>
                <textarea
                  id="home-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={3}
                  placeholder="Fortæl os lidt om dato, antal gæster og dine ønsker..."
                  className="w-full bg-transparent border-b-2 border-white/80 focus:border-white text-white placeholder-white/60 py-2 outline-none transition-colors resize-none font-medium"
                />
              </div>

              {/* Honeypot field (absolutely hidden from humans) */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="home-website_url">Website URL</label>
                <input
                  type="text"
                  id="home-website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {submitError && (
                <p className="text-brand-primary text-sm font-semibold bg-white/10 px-4 py-2 rounded-lg">
                  {submitError}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-2 w-full sm:w-auto self-start flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-brand-dark font-semibold px-8 py-3.5 rounded-full transition-all shadow-md disabled:opacity-50"
              >
                {isSubmitting ? 'Sender...' : 'Send forespørgsel'}
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}
